import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  MotivoRechazoOrdenTrabajo,
  MotivoRechazoOrdenTrabajoPagingRes,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchMotivosRechazoOrdenTrabajo = (
  params?: GetMotivosRechazoOTParams
) => {
  return useQuery({
    queryKey: ['motivo-rechazo-orden-trabajo', ...Object.values(params || {})],
    queryFn: async () => {
      return getMotivosRechazoOrdenTrabajo(params);
    },
  });
};

export const useGetMotivoRechazoOrdenTrabajo = (id: string) => {
  return useQuery({
    queryKey: ['motivo-rechazo-orden-trabajo', id],
    queryFn: async () => {
      return getMotivoRechazoOrdenTrabajo(id);
    },
  });
};

export const useCreateMotivoRechazoOrdenTrabajo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMotivoRechazoOrdenTrabajo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['motivo-rechazo-orden-trabajo'],
      });

      navigate(returnUrl, { replace: true });
    },
  });
};

export const useUpdateMotivoRechazoOrdenTrabajo = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMotivoRechazoOrdenTrabajo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['motivo-rechazo-orden-trabajo'],
      });

      navigate(returnUrl);
    },
  });
};

///* axios
export type GetMotivosRechazoOTParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};
export type CreateMotivoRechazoOTParams = Omit<
  MotivoRechazoOrdenTrabajo,
  'id_motivorechazo_orden'
>;
export type UpdateMotivoRechazoOTParams = {
  id: string;
  data: CreateMotivoRechazoOTParams;
};

export const getMotivosRechazoOrdenTrabajo = async (
  params?: GetMotivosRechazoOTParams
) => {
  const { page = 1, page_size = 10, nombre = '' } = params || {};

  return get<MotivoRechazoOrdenTrabajoPagingRes>(
    `${urlBackend}api/v1/motivo-rechazo-orden/?page=${page}&page_size=${page_size}&nombre=${nombre}`,
    true
  );
};

export const getMotivoRechazoOrdenTrabajo = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<MotivoRechazoOrdenTrabajo>(
    `${urlBackend}api/v1/motivo-rechazo-orden/${id}`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createMotivoRechazoOrdenTrabajo = async (
  data: GetMotivosRechazoOTParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<MotivoRechazoOrdenTrabajo>(
    `${urlBackend}api/v1/motivo-rechazo-orden/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateMotivoRechazoOrdenTrabajo = async ({
  id,
  data,
}: UpdateMotivoRechazoOTParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<MotivoRechazoOrdenTrabajo>(
    `${urlBackend}api/v1/motivo-rechazo-orden/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
